import axios from 'axios'
import { ReactNode } from 'react'
import { atom, selector } from 'recoil'

export type User = {
  email: string
  externalAuthId: string
  expiration: number
  environment: string
}

type ModalContent = {
  title?: string
  content: ReactNode
  showCloseButton?: boolean
}

type Snackbar = {
  message: string | null
  severity?: 'error' | 'info' | 'success' | 'warning'
}

export const getCookies = (): Record<string, string> => {
  return document.cookie.split(';').reduce((acc, curr) => {
    const [_key, value] = curr.split('=')
    const key = _key.trim()
    return { ...acc, [key]: value }
  }, {})
}

export const userState = selector<User | null>({
  key: 'userState',
  get: async () => {
    const response = await axios.get<{
      email: string
      externalAuthId: string
      expiration: number
      environment: string
    }>('/api/user-info')
    return {
      email: response.data.email,
      externalAuthId: response.data.externalAuthId,
      expiration: response.data.expiration,
      environment: response.data.environment,
    }
  },
})

const modalContentInternal = atom<ModalContent>({
  key: 'modalContentInternal',
  default: {
    content: null,
    showCloseButton: false,
  },
})

export const modalContentState = selector<ModalContent>({
  key: 'modalContent',
  get: ({ get }) => {
    return get(modalContentInternal)
  },
  set: ({ set }, value) => {
    set(modalOpenState, true)
    set(modalContentInternal, value)
  },
})

export const modalOpenState = atom<boolean>({
  key: 'modalOpen',
  default: false,
})

const snackbarInternal = atom<Snackbar>({
  key: 'snackbarInternal',
  default: {
    message: null,
  },
})

export const snackbarMessageState = selector<Snackbar>({
  key: 'snackbarMessage',
  get: ({ get }) => {
    return get(snackbarInternal)
  },
  set: ({ set }, value) => {
    set(snackbarInternal, value)
  },
})

const rightDrawerContentInternal = atom<DrawerContent>({
  key: 'rightDrawerContentInternal',
  default: {
    title: '',
    content: null,
  },
})

type DrawerContent = {
  title: string
  content: ReactNode
}

export const rightDrawerContent = selector<DrawerContent>({
  key: 'rightDrawerContent',
  get: ({ get }) => {
    return get(rightDrawerContentInternal)
  },
  set: ({ set }, value) => {
    set(rightDrawerOpen, true)
    set(rightDrawerContentInternal, value)
  },
})

const rightDrawerOpenInternal = atom<boolean>({
  key: 'rightDrawerOpenInternal',
  default: false,
})

export const rightDrawerOpen = selector<boolean>({
  key: 'rightDrawerOpen',
  get: ({ get }) => {
    return get(rightDrawerOpenInternal)
  },
  set: ({ set }, value) => {
    set(rightDrawerOpenInternal, value)
    set(rightDrawerContentInternal, {
      title: '',
      content: null,
    })
  },
})
